import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';

// Auth setup (must be imported before any auth-dependent components)
import './auth';

// Auth context
import { AuthProvider } from './context/AuthContext';

// App pages
import AppLogin from './pages/app/AppLogin';
import AppGroups from './pages/app/AppGroups';
import AppGroupDetail from './pages/app/AppGroupDetail';
import AppCreateGroup from './pages/app/AppCreateGroup';
import AppEvents from './pages/app/AppEvents';
import AppEventDetail from './pages/app/AppEventDetail';
import AppCreateEvent from './pages/app/AppCreateEvent';
import AppServices from './pages/app/AppServices';
import AppServiceDetail from './pages/app/AppServiceDetail';
import AppCreateService from './pages/app/AppCreateService';
import AppMarketplace from './pages/app/AppMarketplace';
import AppMarketplaceDetail from './pages/app/AppMarketplaceDetail';
import AppCreateListing from './pages/app/AppCreateListing';
import AppSettings from './pages/app/AppSettings';
import AppSettingsProfile from './pages/app/AppSettingsProfile';
import AppSettingsDeleteAccount from './pages/app/AppSettingsDeleteAccount';
import AuthGuard from './components/app/AuthGuard';

// Components
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Features from './pages/Features';
import About from './pages/About';
import Contact from './pages/Contact';
import Download from './pages/Download';
import KnowMore from './pages/KnowMore';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import UserManual from './pages/UserManual';
import CookiePolicy from './pages/CookiePolicy';
import GDPR from './pages/GDPR';

// Listens for OAuth redirect back to '/' and forwards to /app/groups
function OAuthRedirectHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        navigate('/groups', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  return null;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <OAuthRedirectHandler />
        <Routes>
          {/* App routes (groups web app) */}
          <Route path="/login" element={<AppLogin />} />
          <Route path="/groups" element={<AppGroups />} />
          <Route
            path="/groups/create"
            element={
              <AuthGuard>
                <AppCreateGroup />
              </AuthGuard>
            }
          />
          <Route
            path="/groups/edit"
            element={
              <AuthGuard>
                <AppCreateGroup />
              </AuthGuard>
            }
          />
          <Route path="/groups/:id" element={<AppGroupDetail />} />
          <Route path="/events" element={<AppEvents />} />
          <Route path="/events/create" element={<AuthGuard><AppCreateEvent /></AuthGuard>} />
          <Route path="/events/edit" element={<AuthGuard><AppCreateEvent /></AuthGuard>} />
          <Route path="/events/:id" element={<AppEventDetail />} />
          <Route path="/services" element={<AppServices />} />
          <Route path="/services/create" element={<AuthGuard><AppCreateService /></AuthGuard>} />
          <Route path="/services/edit" element={<AuthGuard><AppCreateService /></AuthGuard>} />
          <Route path="/services/:id" element={<AppServiceDetail />} />
          <Route path="/marketplace" element={<AppMarketplace />} />
          <Route path="/marketplace/create" element={<AuthGuard><AppCreateListing /></AuthGuard>} />
          <Route path="/marketplace/edit" element={<AuthGuard><AppCreateListing /></AuthGuard>} />
          <Route path="/marketplace/:id" element={<AppMarketplaceDetail />} />
          <Route path="/settings" element={<AuthGuard><AppSettings /></AuthGuard>} />
          <Route path="/settings/profile" element={<AuthGuard><AppSettingsProfile /></AuthGuard>} />
          <Route path="/settings/delete-account" element={<AuthGuard><AppSettingsDeleteAccount /></AuthGuard>} />

          {/* Marketing / existing routes */}
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/about" element={<About />} />
          <Route path="/know-more" element={<KnowMore />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/download" element={<Download />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          {/* /events/:id, /services/:id, /marketplace/:id handled above */}
          <Route path="/user-manual" element={<UserManual />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/gdpr" element={<GDPR />} />
        </Routes>
        <Footer />
      </AuthProvider>
    </Router>
  );
}

export default App;
